import { SchemaGetter } from "effect";
import { Schema } from "effect";

const canonicalizeHexColor = (value: string): string =>
  value.trim().toUpperCase();

const isCanonicalHexColor = (value: string): boolean =>
  /^#[0-9A-F]{6}$/.test(value);

const canonicalizeAssetHash = (value: string): string =>
  value.trim().toLowerCase();

const isAssetHash = (value: string): boolean => /^[0-9a-f]{64}$/.test(value);

const normalizeDisplayName = (value: string): string => value.trim();

const isDisplayName = (value: string): boolean =>
  value.length > 0 && value.length <= 80;

/**
 * Canonical uppercase six-digit sRGB brand color.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { BrandColor } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * Schema.decodeUnknownSync(BrandColor)(" #223381 "); // "#223381"
 * ```
 */
export const BrandColor = Schema.String.pipe(Schema.decodeTo(Schema.String, {

  decode: SchemaGetter.transform(canonicalizeHexColor),
  encode: SchemaGetter.transform((value) => value),
})).pipe(
  Schema.check(Schema.makeFilter(isCanonicalHexColor, {
    identifier: "BrandColor",
    description: "An uppercase #RRGGBB sRGB color",
  })),
  Schema.brand("BrandColor"),
);

/**
 * Decoded canonical brand color.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandColor } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function render(color: BrandColor): string { return color; }
 * ```
 */
export type BrandColor = typeof BrandColor.Type;

/**
 * Lowercase SHA-256 content identifier for an immutable managed asset.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { BrandAssetHash } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * Schema.decodeUnknownSync(BrandAssetHash)("a".repeat(64));
 * ```
 */
export const BrandAssetHash = Schema.String.pipe(Schema.decodeTo(Schema.String, {

  decode: SchemaGetter.transform(canonicalizeAssetHash),
  encode: SchemaGetter.transform((value) => value),
})).pipe(
  Schema.check(Schema.makeFilter(isAssetHash, {
    identifier: "BrandAssetHash",
    description: "A lowercase 64-character SHA-256 digest",
  })),
  Schema.brand("BrandAssetHash"),
);

/**
 * Decoded immutable asset identifier.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandAssetHash } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function assetPath(hash: BrandAssetHash): string { return `/branding/assets/${hash}`; }
 * ```
 */
export type BrandAssetHash = typeof BrandAssetHash.Type;

/**
 * Optional practice-facing display name kept separate from the legal name.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { BrandDisplayName } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * Schema.decodeUnknownSync(BrandDisplayName)(" Praxis am Markt ");
 * ```
 */
export const BrandDisplayName = Schema.String.pipe(Schema.decodeTo(Schema.String, {

  decode: SchemaGetter.transform(normalizeDisplayName),
  encode: SchemaGetter.transform((value) => value),
})).pipe(
  Schema.check(Schema.makeFilter(isDisplayName, {
    identifier: "BrandDisplayName",
    description: "A non-empty display name of at most 80 characters",
  })),
  Schema.brand("BrandDisplayName"),
);

/**
 * Decoded visual practice name.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandDisplayName } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function heading(name: BrandDisplayName): string { return name; }
 * ```
 */
export type BrandDisplayName = typeof BrandDisplayName.Type;

/**
 * Accent or supporting color input selected explicitly or generated automatically.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { BrandColorRoleInput } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * Schema.decodeUnknownSync(BrandColorRoleInput)({ mode: "automatic" });
 * ```
 */
export const BrandColorRoleInput = Schema.Union(
  [Schema.Struct({ mode: Schema.Literal("automatic") }),
  Schema.Struct({ mode: Schema.Literal("explicit"), seed: BrandColor })],
);

/**
 * Decoded color-role input.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandColorRoleInput } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * const role: BrandColorRoleInput = { mode: "automatic" };
 * ```
 */
export type BrandColorRoleInput = typeof BrandColorRoleInput.Type;

/**
 * Curated three-role brand color inputs.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { BrandColorInputs } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * Schema.decodeUnknownSync(BrandColorInputs)({
 *   primary: "#223381", accent: { mode: "automatic" }, supporting: { mode: "automatic" },
 * });
 * ```
 */
export const BrandColorInputs = Schema.Struct({
  primary: BrandColor,
  accent: BrandColorRoleInput,
  supporting: BrandColorRoleInput,
});

/**
 * Decoded curated color inputs.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandColorInputs } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function primaryOf(colors: BrandColorInputs) { return colors.primary; }
 * ```
 */
export type BrandColorInputs = typeof BrandColorInputs.Type;

/**
 * Built-in font families shipped by FrontDesk+.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { BuiltinBrandFontId } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * Schema.decodeUnknownSync(BuiltinBrandFontId)("geist-variable");
 * ```
 */
export const BuiltinBrandFontId = Schema.Literals(
  ["ryker",
  "neue-haas-display",
  "geist-variable",
  "fraunces-variable"],
);

/**
 * Decoded built-in font identifier.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BuiltinBrandFontId } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * const font: BuiltinBrandFontId = "geist-variable";
 * ```
 */
export type BuiltinBrandFontId = typeof BuiltinBrandFontId.Type;

/**
 * Heading or body font selection from the built-in or managed catalog.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { BrandFontSelection } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * Schema.decodeUnknownSync(BrandFontSelection)({ source: "builtin", familyId: "geist-variable" });
 * ```
 */
export const BrandFontSelection = Schema.Union(
  [Schema.Struct({
    source: Schema.Literal("builtin"),
    familyId: BuiltinBrandFontId,
  }),
  Schema.Struct({
    source: Schema.Literal("managed"),
    familyId: BrandAssetHash,
  })],
);

/**
 * Decoded heading or body font selection.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandFontSelection } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * const selection: BrandFontSelection = { source: "builtin", familyId: "fraunces-variable" };
 * ```
 */
export type BrandFontSelection = typeof BrandFontSelection.Type;

/**
 * Editable practice-branding draft with explicit default/custom state.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { BrandingDraft } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * Schema.decodeUnknownSync(BrandingDraft)({ kind: "default" });
 * ```
 */
export const BrandingDraft = Schema.Union(
  [Schema.Struct({ kind: Schema.Literal("default") }),
  Schema.Struct({
    kind: Schema.Literal("custom"),
    displayName: Schema.optional(BrandDisplayName),
    colors: BrandColorInputs,
    primaryLogo: BrandAssetHash,
    compactMark: Schema.optional(BrandAssetHash),
    headingFont: BrandFontSelection,
    bodyFont: BrandFontSelection,
  })],
);

/**
 * Decoded editable branding draft.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingDraft } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * const draft: BrandingDraft = { kind: "default" };
 * ```
 */
export type BrandingDraft = typeof BrandingDraft.Type;

/**
 * Fixed light-mode semantic tier derived from one source seed.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandSemanticTier } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function baseOf(tier: BrandSemanticTier) { return tier.base; }
 * ```
 */
export const BrandSemanticTier = Schema.Struct({
  base: BrandColor,
  foreground: BrandColor,
  soft: BrandColor,
  subtle: BrandColor,
  vivid: BrandColor,
  ink: BrandColor,
  neon: BrandColor,
});

/**
 * Decoded semantic color tier.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandSemanticTier } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function foregroundOf(tier: BrandSemanticTier) { return tier.foreground; }
 * ```
 */
export type BrandSemanticTier = typeof BrandSemanticTier.Type;

/**
 * Complete fixed set of practice-controlled light-mode color tokens.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { DerivedBrandColorTokens } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function buttonColor(tokens: DerivedBrandColorTokens) { return tokens.primary.base; }
 * ```
 */
export const DerivedBrandColorTokens = Schema.Struct({
  primary: BrandSemanticTier,
  accent: BrandSemanticTier,
  supporting: BrandSemanticTier,
  interactionAccent: Schema.Struct({
    base: BrandColor,
    foreground: BrandColor,
  }),
});

/**
 * Decoded fixed practice-controlled color tokens.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { DerivedBrandColorTokens } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function interaction(tokens: DerivedBrandColorTokens) { return tokens.interactionAccent.base; }
 * ```
 */
export type DerivedBrandColorTokens = typeof DerivedBrandColorTokens.Type;

/**
 * Browser font-face descriptor without a caller-controlled URL.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandFontFaceDescriptor } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function weight(face: BrandFontFaceDescriptor) { return face.weightMin; }
 * ```
 */
export const BrandFontFaceDescriptor = Schema.Struct({
  assetHash: Schema.optional(BrandAssetHash),
  mediaType: Schema.optional(Schema.Literals(["font/woff2", "font/ttf", "font/otf"])),
  weightMin: Schema.Int.pipe(Schema.check(Schema.isBetween({ minimum: 1, maximum: 1000 }))),
  weightMax: Schema.Int.pipe(Schema.check(Schema.isBetween({ minimum: 1, maximum: 1000 }))),
  style: Schema.Literal("normal"),
});

/**
 * Decoded browser font-face descriptor.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandFontFaceDescriptor } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * const face: BrandFontFaceDescriptor = { weightMin: 100, weightMax: 900, style: "normal" };
 * ```
 */
export type BrandFontFaceDescriptor = typeof BrandFontFaceDescriptor.Type;

/**
 * Fully resolved heading or body font family descriptor.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { ResolvedBrandFont } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function cssFamily(font: ResolvedBrandFont) { return font.cssFamily; }
 * ```
 */
export const ResolvedBrandFont = Schema.Union(
  [Schema.Struct({
    source: Schema.Literal("builtin"),
    familyId: BuiltinBrandFontId,
    cssFamily: Schema.String.pipe(Schema.check(Schema.isMinLength(1)), Schema.check(Schema.isMaxLength(120))),
    fallback: Schema.String.pipe(Schema.check(Schema.isMinLength(1)), Schema.check(Schema.isMaxLength(240))),
    display: Schema.Literal("swap"),
    faces: Schema.NonEmptyArray(BrandFontFaceDescriptor),
  }),
  Schema.Struct({
    source: Schema.Literal("managed"),
    familyId: BrandAssetHash,
    cssFamily: Schema.String.pipe(Schema.check(Schema.isMinLength(1)), Schema.check(Schema.isMaxLength(120))),
    fallback: Schema.String.pipe(Schema.check(Schema.isMinLength(1)), Schema.check(Schema.isMaxLength(240))),
    display: Schema.Literal("swap"),
    faces: Schema.NonEmptyArray(BrandFontFaceDescriptor),
  })],
);

/**
 * Decoded resolved brand font family.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { ResolvedBrandFont } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function family(font: ResolvedBrandFont) { return font.familyId; }
 * ```
 */
export type ResolvedBrandFont = typeof ResolvedBrandFont.Type;

/**
 * Fixed heading and body font roles resolved for a published brand.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { DerivedBrandFonts } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function heading(fonts: DerivedBrandFonts) { return fonts.heading; }
 * ```
 */
export const DerivedBrandFonts = Schema.Struct({
  heading: ResolvedBrandFont,
  body: ResolvedBrandFont,
});

/**
 * Decoded resolved heading and body font roles.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { DerivedBrandFonts } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function body(fonts: DerivedBrandFonts) { return fonts.body; }
 * ```
 */
export type DerivedBrandFonts = typeof DerivedBrandFonts.Type;

/**
 * Public immutable metadata for a validated practice logo.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandLogoDescriptor } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function width(logo: BrandLogoDescriptor) { return logo.width; }
 * ```
 */
export const BrandLogoDescriptor = Schema.Struct({
  assetHash: BrandAssetHash,
  mediaType: Schema.Literals(["image/png", "image/webp", "image/svg+xml"]),
  byteLength: Schema.Int.pipe(Schema.check(Schema.isGreaterThan(0))),
  width: Schema.Number.pipe(Schema.check(Schema.isGreaterThan(0))),
  height: Schema.Number.pipe(Schema.check(Schema.isGreaterThan(0))),
});

/**
 * Decoded validated logo metadata.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandLogoDescriptor } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function mediaType(logo: BrandLogoDescriptor) { return logo.mediaType; }
 * ```
 */
export type BrandLogoDescriptor = typeof BrandLogoDescriptor.Type;

/**
 * Public immutable metadata for a managed WOFF2 font face.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandFontAssetDescriptor } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function fontName(font: BrandFontAssetDescriptor) { return font.familyName; }
 * ```
 */
export const BrandFontAssetDescriptor = Schema.Struct({
  assetHash: BrandAssetHash,
  mediaType: Schema.Literals(["font/woff2", "font/ttf", "font/otf"]),
  byteLength: Schema.Int.pipe(Schema.check(Schema.isGreaterThan(0))),
  familyName: Schema.String.pipe(Schema.check(Schema.isMinLength(1)), Schema.check(Schema.isMaxLength(120))),
  postscriptName: Schema.String.pipe(Schema.check(Schema.isMinLength(1)), Schema.check(Schema.isMaxLength(160))),
  weightMin: Schema.Int.pipe(Schema.check(Schema.isBetween({ minimum: 1, maximum: 1000 }))),
  weightMax: Schema.Int.pipe(Schema.check(Schema.isBetween({ minimum: 1, maximum: 1000 }))),
  style: Schema.Literal("normal"),
});

/**
 * Decoded managed WOFF2 metadata.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandFontAssetDescriptor } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function range(font: BrandFontAssetDescriptor) { return [font.weightMin, font.weightMax]; }
 * ```
 */
export type BrandFontAssetDescriptor = typeof BrandFontAssetDescriptor.Type;

/**
 * Stable validation finding codes shared by administration and publication.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingValidationFindingCode } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * const code: BrandingValidationFindingCode = "asset-format-unsupported";
 * ```
 */
export const BrandingValidationFindingCode = Schema.Literals(
  ["draft-invalid",
  "primary-logo-required",
  "asset-not-found",
  "asset-too-large",
  "asset-format-unsupported",
  "asset-corrupt",
  "logo-geometry-missing",
  "svg-unsafe",
  "font-rights-unacknowledged",
  "font-not-woff2",
  "font-italic-unsupported",
  "font-family-mismatch",
  "font-weight-coverage-incomplete",
  "font-family-full",
  "font-face-duplicate",
  "contrast-text-insufficient",
  "contrast-boundary-insufficient",
  "semantic-color-derived",
  "font-glyph-fallback"],
);

/**
 * Decoded validation finding code.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingValidationFindingCode } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * const code: BrandingValidationFindingCode = "semantic-color-derived";
 * ```
 */
export type BrandingValidationFindingCode =
  typeof BrandingValidationFindingCode.Type;

/**
 * Typed candidate or asset validation finding safe to show in SOL administration.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingValidationFinding } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * const finding: BrandingValidationFinding = {
 *   severity: "error", code: "asset-corrupt", field: "primaryLogo", message: "Invalid image.",
 * };
 * ```
 */
export const BrandingValidationFinding = Schema.Struct({
  severity: Schema.Literals(["error", "warning"]),
  code: BrandingValidationFindingCode,
  field: Schema.String.pipe(Schema.check(Schema.isMinLength(1)), Schema.check(Schema.isMaxLength(120))),
  message: Schema.String.pipe(Schema.check(Schema.isMinLength(1)), Schema.check(Schema.isMaxLength(500))),
});

/**
 * Decoded branding validation finding.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingValidationFinding } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function isError(finding: BrandingValidationFinding) { return finding.severity === "error"; }
 * ```
 */
export type BrandingValidationFinding = typeof BrandingValidationFinding.Type;

/**
 * Measured accessibility evidence for a required brand color pair.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingContrastEvidence } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function passes(evidence: BrandingContrastEvidence) { return evidence.ratio >= evidence.required; }
 * ```
 */
export const BrandingContrastEvidence = Schema.Struct({
  pair: Schema.String.pipe(Schema.check(Schema.isMinLength(1)), Schema.check(Schema.isMaxLength(80))),
  foreground: BrandColor,
  background: BrandColor,
  ratio: Schema.Number.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(1))),
  required: Schema.Literals([3, 4.5]),
  kind: Schema.Literals(["text", "boundary"]),
});

/**
 * Decoded contrast evidence.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { BrandingContrastEvidence } from "@ascleo/frontdesk-contracts/branding/model";
 *
 * function pair(evidence: BrandingContrastEvidence) { return evidence.pair; }
 * ```
 */
export type BrandingContrastEvidence = typeof BrandingContrastEvidence.Type;
